import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { Calendar, Play, Zap, CheckCircle, XCircle, Clock, Timer } from 'lucide-react';
import type { ComputationType } from './types';
import { COMPUTATION_OPTIONS, daysAgo, today, safeLocaleNumber } from './types';

interface ComputeResponse {
  results: Record<string, number | { error: string }>;
}

interface ResultEntry {
  key: string;
  status: 'success' | 'error';
  rowCount: number;
  error?: string;
}

interface HistoryEntry {
  timestamp: string;
  totalRows: number;
  status: 'success' | 'error';
  dayRange: string;
}

function parseResults(raw: Record<string, number | { error: string }>): ResultEntry[] {
  return Object.entries(raw).map(([key, val]) => {
    if (typeof val === 'number') {
      return { key, status: 'success' as const, rowCount: val };
    }
    return { key, status: 'error' as const, rowCount: 0, error: val.error };
  });
}

function estimateTime(days: number, computations: number): string {
  const base = Math.max(10, days * 0.5 * computations);
  if (base < 60) return `~${Math.round(base)}s`;
  return `~${(base / 60).toFixed(1)}min`;
}

export function ComputationSection({ storeId }: { readonly storeId: number }) {
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());
  const [selected, setSelected] = useState<ReadonlySet<ComputationType>>(
    new Set(['baseline', 'attribution', 'rollup', 'correlation']),
  );
  const [results, setResults] = useState<readonly ResultEntry[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [history, setHistory] = useState<readonly HistoryEntry[]>([]);

  function toggleComputation(key: ComputationType) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  const computeMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<ComputeResponse>(
        '/api/v1/ingestion/admin/compute',
        { storeId, startDate, endDate, computations: [...selected] },
      );
      if (!res.success || !res.data) throw new Error(res.error ?? 'Computation failed');
      return res.data;
    },
    onSuccess: (data) => {
      const parsed = parseResults(data.results);
      setResults(parsed);
      const allOk = parsed.every((r) => r.status === 'success');
      const totalRows = parsed.reduce((s, r) => s + r.rowCount, 0);
      setMessage({
        type: allOk ? 'success' : 'error',
        text: allOk
          ? `Completed -- ${safeLocaleNumber(totalRows)} total rows written`
          : 'Completed with errors -- check results below',
      });
      setHistory((prev) => [
        {
          timestamp: new Date().toISOString(),
          totalRows,
          status: allOk ? 'success' : 'error',
          dayRange: `${startDate} to ${endDate}`,
        },
        ...prev.slice(0, 2),
      ]);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'halo-diagnostics', storeId] });
      setTimeout(() => setMessage(null), 8000);
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message });
      setResults([]);
      setTimeout(() => setMessage(null), 5000);
    },
  });

  function handleQuickAction(days: number | 'full') {
    const newStart = days === 'full' ? '2020-01-01' : daysAgo(days);
    setStartDate(newStart);
    setEndDate(today());
    setSelected(new Set(['baseline', 'attribution', 'rollup', 'correlation']));
  }

  const dayCount = Math.max(0, Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000) + 1);
  const progressWidth = computeMutation.isPending ? 100 : 0;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 flex items-center justify-center">
            <Play className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Manual Computation</h3>
        </div>
        <div className="flex items-center gap-2">
          {dayCount > 0 && selected.size > 0 && (
            <span className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500">
              <Timer className="w-3 h-3" />
              {estimateTime(dayCount, selected.size)}
            </span>
          )}
          {dayCount > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
              {dayCount} day{dayCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {computeMutation.isPending && (
        <div className="h-1 bg-gray-100 dark:bg-gray-800 rounded-full mb-4 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 rounded-full transition-all duration-[2000ms] ease-linear animate-pulse"
            style={{ width: `${progressWidth}%`, backgroundSize: '200% 100%' }}
          />
        </div>
      )}

      {/* Date Range */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300 block mb-1.5">Start Date</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500 pointer-events-none" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-gray-50 dark:bg-gray-950 focus:bg-white dark:focus:bg-gray-900 transition-all"
            />
          </div>
        </div>
        <div className="flex-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300 block mb-1.5">End Date</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500 pointer-events-none" />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-gray-50 dark:bg-gray-950 focus:bg-white dark:focus:bg-gray-900 transition-all"
            />
          </div>
        </div>
      </div>

      {/* Computation Chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {COMPUTATION_OPTIONS.map((opt) => {
          const isActive = selected.has(opt.key);
          return (
            <button
              key={opt.key}
              onClick={() => toggleComputation(opt.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                isActive
                  ? 'bg-indigo-50 text-indigo-700 dark:text-indigo-400 border-indigo-200'
                  : 'bg-white text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-800 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Run Button */}
      <button
        onClick={() => computeMutation.mutate()}
        disabled={computeMutation.isPending || selected.size === 0}
        className="w-full px-4 py-2.5 text-sm font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
      >
        {computeMutation.isPending ? (
          <>
            <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Computing...
          </>
        ) : (
          <>
            <Play className="w-3.5 h-3.5" />
            Run Computations
          </>
        )}
      </button>

      {/* Message */}
      {message && (
        <div className={`flex items-center gap-2 mt-3 px-3 py-2.5 rounded-lg text-sm border ${
          message.type === 'success'
            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
            : 'bg-red-50 text-red-800 dark:text-red-400 border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
          {message.text}
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="mt-4 space-y-1.5">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Results</span>
          {results.map((r) => (
            <div
              key={r.key}
              className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm border ${
                r.status === 'success'
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-100'
                  : 'bg-red-50 text-red-800 dark:text-red-400 border-red-100'
              }`}
            >
              <span className="font-medium capitalize">{r.key}</span>
              <span className="text-xs">
                {r.status === 'success' ? `${safeLocaleNumber(r.rowCount)} rows` : r.error ?? 'Failed'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2 mb-2.5">
          <Zap className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Quick Actions</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleQuickAction(30)}
            className="px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950 hover:border-gray-300 dark:hover:border-gray-700 transition-all"
          >
            Last 30 Days
          </button>
          <button
            onClick={() => handleQuickAction(90)}
            className="px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950 hover:border-gray-300 dark:hover:border-gray-700 transition-all"
          >
            Last 90 Days
          </button>
          <button
            onClick={() => handleQuickAction('full')}
            className="px-3 py-1.5 text-xs font-medium border border-amber-200 dark:border-amber-900/50 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/40 text-amber-700 dark:text-amber-300 transition-all"
          >
            Full Recompute
          </button>
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2 mb-2.5">
            <Clock className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Recent Runs</span>
          </div>
          <div className="space-y-1.5">
            {history.map((entry, i) => (
              <div key={i} className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 py-1.5 px-2 bg-gray-50 dark:bg-gray-950 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${entry.status === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  <span>{new Date(entry.timestamp).toLocaleTimeString()}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 dark:text-gray-500">{entry.dayRange}</span>
                  <span className="font-medium">{safeLocaleNumber(entry.totalRows)} rows</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
